import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Button, styled } from '@mui/material';
import { LINKS } from '../lib/links.js';

// Styled components with premium animations
const HeroSection = styled(Box)(({ theme }) => ({
    position: 'relative',
    padding: '0 0 36px',
    minHeight: 'auto',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFF 100%)',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '10%',
        left: '0',
        width: '100%',
        height: '80%',
        background: 'radial-gradient(ellipse 60% 40% at 30% 50%, rgba(0, 102, 255, 0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
        animation: 'breathe 15s ease-in-out infinite',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: '20%',
        right: '5%',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.04) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        animation: 'float 20s ease-in-out infinite',
        pointerEvents: 'none',
    },
    '@keyframes breathe': {
        '0%, 100%': {
            opacity: 1,
            transform: 'scale(1)',
        },
        '50%': {
            opacity: 0.7,
            transform: 'scale(1.15)',
        },
    },
    '@keyframes float': {
        '0%, 100%': {
            transform: 'translate(0, 0)',
        },
        '50%': {
            transform: 'translate(-50px, 30px)',
        },
    },
    '@media (max-width: 968px)': {
        padding: '60px 0 70px',
        minHeight: 'auto',
    },
    '@media (max-width: 600px)': {
        padding: '60px 0 50px',
    },
}));

const HeroGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '80px',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
    '@media (max-width: 968px)': {
        gridTemplateColumns: '1fr',
        gap: '56px',
    },
}));

const HeroContent = styled(Box)(({ theme }) => ({
    position: 'relative',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-100px',
        left: '-100px',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(0, 102, 255, 0.06) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'orbitFloat 20s ease-in-out infinite',
        pointerEvents: 'none',
        zIndex: -1,
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-80px',
        right: '-80px',
        width: '250px',
        height: '250px',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(50px)',
        animation: 'orbitFloat 15s ease-in-out infinite reverse',
        pointerEvents: 'none',
        zIndex: -1,
    },
    '@keyframes orbitFloat': {
        '0%, 100%': {
            transform: 'translate(0, 0) scale(1)',
        },
        '33%': {
            transform: 'translate(30px, -30px) scale(1.1)',
        },
        '66%': {
            transform: 'translate(-20px, 20px) scale(0.95)',
        },
    },
    '@media (max-width: 968px)': {
        textAlign: 'center',
        alignItems: 'center',
        '&::before, &::after': {
            display: 'none',
        },
    },
}));


const HeroTitle = styled(Typography)(({ theme }) => ({
    fontSize: '60px',
    fontWeight: 600,
    lineHeight: 1.1,
    marginBottom: '32px',
    color: '#111827',
    letterSpacing: '-2px',
    animation: 'titleReveal 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards',
    position: 'relative',
    opacity: 0,
    '& br': {
        display: 'block',
    },
    '@keyframes titleReveal': {
        '0%': {
            opacity: 0,
            transform: 'translateY(60px) scale(0.9)',
            filter: 'blur(15px)',
        },
        '60%': {
            transform: 'translateY(-5px) scale(1.02)',
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0) scale(1)',
            filter: 'blur(0)',
        },
    },
    '@media (max-width: 968px)': {
        fontSize: '48px',
        letterSpacing: '-1.5px',
    },
    '@media (max-width: 600px)': {
        fontSize: '38px',
        letterSpacing: '-1.2px',
        lineHeight: 1.15,
    },
    '@media (max-width: 480px)': {
        fontSize: '32px',
        letterSpacing: '-1px',
    },
}));

const GradientText = styled('span')({
    background: 'linear-gradient(90deg, #0066FF 0%, #00D4FF 25%, #6366F1 50%, #00D4FF 75%, #0066FF 100%)',
    backgroundSize: '300% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    position: 'relative',
    display: 'inline-block',
    animation: 'gradientWave 4s ease-in-out infinite, textPulse 3s ease-in-out infinite',
    filter: 'drop-shadow(0 0 20px rgba(0, 102, 255, 0.3))',
    '@keyframes gradientWave': {
        '0%, 100%': {
            backgroundPosition: '0% center',
        },
        '50%': {
            backgroundPosition: '100% center',
        },
    },
    '@keyframes textPulse': {
        '0%, 100%': {
            transform: 'scale(1)',
        },
        '50%': {
            transform: 'scale(1.02)',
        },
    },
});

const HeroSubtitle = styled(Typography)(({ theme }) => ({
    fontSize: '20px',
    lineHeight: 1.6,
    color: '#6B7280',
    marginBottom: '18px',
    maxWidth: '650px',
    textAlign: 'justify',
    fontWeight: 400,
    position: 'relative',
    '@media (max-width: 968px)': {
        fontSize: '18px',
        maxWidth: '100%',
        margin: '0 auto 40px',
    },
    '@media (max-width: 600px)': {
        fontSize: '17px',
        padding: '0 10px',
        lineHeight: 1.55,
    },
    '@media (max-width: 480px)': {
        fontSize: '16px',
    },
}));

const HeroCTA = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    animation: 'ctaReveal 1.5s cubic-bezier(0.16, 1, 0.3, 1) 0.8s forwards',
    opacity: 0,
    '@keyframes ctaReveal': {
        '0%': {
            opacity: 0,
            transform: 'translateY(40px) scale(0.8)',
            filter: 'blur(10px)',
        },
        '70%': {
            transform: 'translateY(-10px) scale(1.05)',
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0) scale(1)',
            filter: 'blur(0)',
        },
    },
    '@media (max-width: 968px)': {
        flexDirection: 'column',
        width: '100%',
        gap: '12px',
    },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
    padding: '16px 32px',
    fontSize: '15px',
    fontWeight: 600,
    borderRadius: '12px',
    textTransform: 'none',
    background: 'linear-gradient(135deg, #0066FF, #0052CC)',
    color: '#FFFFFF',
    boxShadow: '0 4px 20px rgba(0, 102, 255, 0.3), 0 0 40px rgba(0, 102, 255, 0.1)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    minWidth: '180px',
    letterSpacing: '-0.2px',
    animation: 'buttonPulse 3s ease-in-out infinite',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '0',
        height: '0',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.4)',
        transform: 'translate(-50%, -50%)',
        transition: 'width 0.6s ease-out, height 0.6s ease-out',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '12px',
        padding: '2px',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.1))',
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        opacity: 0.5,
    },
    '@keyframes buttonPulse': {
        '0%, 100%': {
            boxShadow: '0 4px 20px rgba(0, 102, 255, 0.3), 0 0 40px rgba(0, 102, 255, 0.1)',
        },
        '50%': {
            boxShadow: '0 8px 32px rgba(0, 102, 255, 0.4), 0 0 60px rgba(0, 102, 255, 0.2)',
        },
    },
    '&:hover': {
        background: 'linear-gradient(135deg, #0052CC, #003D99)',
        boxShadow: '0 12px 40px rgba(0, 102, 255, 0.5), 0 0 80px rgba(0, 102, 255, 0.3)',
        transform: 'translateY(-4px) scale(1.05)',
        animation: 'none',
    },
    '&:hover::before': {
        width: '400px',
        height: '400px',
    },
    '&:active': {
        transform: 'translateY(-2px) scale(1.02)',
        boxShadow: '0 6px 24px rgba(0, 102, 255, 0.4)',
    },
    '@media (max-width: 968px)': {
        width: '100%',
        justifyContent: 'center',
    },
    '@media (max-width: 600px)': {
        padding: '14px 28px',
        fontSize: '15px',
    },
    '@media (max-width: 480px)': {
        padding: '12px 24px',
        fontSize: '14px',
    },
}));

const GhostButton = styled(Button)(({ theme }) => ({
    padding: '16px 32px',
    fontSize: '15px',
    fontWeight: 500,
    borderRadius: '12px',
    textTransform: 'none',
    color: '#374151',
    borderColor: '#D1D5DB',
    borderWidth: '1.5px',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    minWidth: '220px',
    background: '#FFFFFF',
    letterSpacing: '-0.2px',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'rgba(0, 102, 255, 0.04)',
        transform: 'scaleX(0)',
        transformOrigin: 'left',
        transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    '&:hover': {
        borderColor: '#0066FF',
        color: '#0066FF',
        transform: 'translateY(-3px)',
        boxShadow: '0 8px 24px rgba(0, 102, 255, 0.12)',
    },
    '&:hover::before': {
        transform: 'scaleX(1)',
    },
    '&:active': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0, 102, 255, 0.08)',
    },
    '@media (max-width: 968px)': {
        width: '100%',
        justifyContent: 'center',
    },
    '@media (max-width: 600px)': {
        padding: '14px 28px',
        fontSize: '15px',
    },
    '@media (max-width: 480px)': {
        padding: '12px 24px',
        fontSize: '14px',
    },
}));


const HeroVisual = styled(Box)(({ theme }) => ({
    position: 'relative',
    animation: 'visualReveal 1.8s cubic-bezier(0.16, 1, 0.3, 1) 0.6s forwards',
    opacity: 0,
    '@keyframes visualReveal': {
        '0%': {
            opacity: 0,
            transform: 'scale(0.9)',
            filter: 'blur(15px)',
        },
        '100%': {
            opacity: 1,
            transform: 'scale(1)',
            filter: 'blur(0)',
        },
    },
}));

const VideoFrame = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    borderRadius: '24px',
    overflow: 'hidden',
    background: '#FFFFFF',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.04)',
    transition: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1,
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: '-2px',
        borderRadius: '24px',
        padding: '2px',
        background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.3), rgba(99, 102, 241, 0.2))',
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        opacity: 0,
        transition: 'opacity 400ms ease-out',
    },
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 32px 80px rgba(0, 0, 0, 0.12), 0 12px 32px rgba(0, 102, 255, 0.08)',
    },
    '&:hover::before': {
        opacity: 1,
    },
    '& video': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: '24px',
    },
    '@media (max-width: 600px)': {
        borderRadius: '20px',
        paddingTop: '60%',
        '&::before': {
            borderRadius: '20px',
        },
        '& video': {
            borderRadius: '20px',
        },
        '&:hover': {
            transform: 'translateY(-4px)',
        },
    },
}));

const DashboardPreview = styled(Box)(({ theme }) => ({
    marginTop: '120px',
    position: 'relative',
    zIndex: 1,
    animation: 'dashboardReveal 1.8s cubic-bezier(0.16, 1, 0.3, 1) 1s forwards',
    opacity: 0,
    '@keyframes dashboardReveal': {
        '0%': {
            opacity: 0,
            transform: 'translateY(40px) scale(0.98)',
            filter: 'blur(10px)',
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0) scale(1)',
            filter: 'blur(0)',
        },
    },
    '@media (max-width: 968px)': {
        marginTop: '80px',
    },
}));

const DashboardWindow = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    background: '#FFFFFF',
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 102, 255, 0.03)',
    overflow: 'hidden',
    transition: 'all 800ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: '-3px',
        borderRadius: '24px',
        padding: '2px',
        background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.5), rgba(99, 102, 241, 0.3))',
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        opacity: 0,
        transition: 'opacity 600ms ease-out',
    },
    '&:hover': {
        transform: 'translateY(-12px)',
        boxShadow: '0 32px 90px rgba(0, 0, 0, 0.08), 0 12px 40px rgba(0, 102, 255, 0.06)',
    },
    '&:hover::before': {
        opacity: 1,
    },
    '@media (max-width: 600px)': {
        borderRadius: '20px',
        '&::before': {
            borderRadius: '20px',
        },
    },
}));

const DashboardControls = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '12px',
    left: '12px',
    display: 'flex',
    gap: '8px',
    zIndex: 2,
    '@media (max-width: 600px)': {
        top: '8px',
        left: '8px',
        gap: '6px',
    },
}));

const ControlButton = styled(Box)(({ theme, color }) => ({
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: color,
    transition: 'all 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
        transform: 'scale(1.3)',
        boxShadow: `0 0 12px ${color}`,
    },
    '&:active': {
        transform: 'scale(0.9)',
    },
    '@media (max-width: 600px)': {
        width: '10px',
        height: '10px',
    },
}));

const DashboardContent = styled(Box)(({ theme }) => ({
    padding: '48px 0 0 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#FFFFFF',
    '& img': {
        width: '100%',
        height: 'auto',
        display: 'block',
    },
    '@media (max-width: 600px)': {
        padding: '42px 12px 12px 12px',
    },
}));

// Therapist registration — separate section above hero
const TherapistBannerSectionWrap = styled(Box)({
    position: 'relative',
    zIndex: 1,
    padding: '92px 0 16px',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFF 100%)',
    '@media (max-width: 600px)': {
        padding: '90px 0 0',
    },
});

const TherapistBannerRoot = styled(Box)({
    position: 'relative',
    animation: 'bannerReveal 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards',
    opacity: 0,
    '@keyframes bannerReveal': {
        '0%': {
            opacity: 0,
            transform: 'translateY(16px)',
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },
});

const BannerStrip = styled(Box)({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '24px',
    background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.08) 0%, rgba(99, 102, 241, 0.06) 50%, rgba(0, 212, 255, 0.06) 100%)',
    border: '1.5px solid rgba(0, 102, 255, 0.18)',
    padding: '40px 48px 64px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '28px',
    boxShadow: '0 8px 32px rgba(0, 102, 255, 0.08), 0 0 0 1px rgba(255,255,255,0.5) inset',
    transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        borderColor: 'rgba(0, 102, 255, 0.3)',
        boxShadow: '0 12px 40px rgba(0, 102, 255, 0.12), 0 0 0 1px rgba(255,255,255,0.6) inset',
    },
    '@media (max-width: 768px)': {
        padding: '24px 20px 48px',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'center',
        gap: '20px',
    },
    '@media (max-width: 480px)': {
        padding: '20px 16px 40px',
        borderRadius: '20px',
        gap: '16px',
    },
});

const MarqueeTrack = styled(Box)({
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '32px',
    overflow: 'hidden',
    borderTop: '1px solid rgba(0, 102, 255, 0.08)',
    '@media (max-width: 768px)': {
        position: 'relative',
        marginTop: '4px',
        borderTop: 'none',
        borderBottom: '1px solid rgba(0, 102, 255, 0.08)',
        paddingBottom: '8px',
        height: '28px',
    },
});

const MarqueeContent = styled(Box)({
    display: 'flex',
    width: 'max-content',
    animation: 'marquee 35s linear infinite',
    '@keyframes marquee': {
        '0%': { transform: 'translateX(0)' },
        '100%': { transform: 'translateX(-50%)' },
    },
});

const MarqueeItem = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 20px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#64748B',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
});

const GetStartedButton = styled(Button)({
    padding: '14px 32px',
    fontSize: '15px',
    fontWeight: 600,
    borderRadius: '12px',
    textTransform: 'none',
    background: 'linear-gradient(135deg, #0066FF, #0052CC)',
    color: '#FFFFFF',
    boxShadow: '0 4px 20px rgba(0, 102, 255, 0.35)',
    letterSpacing: '-0.2px',
    transition: 'all 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    flexShrink: 0,
    '&:hover': {
        background: 'linear-gradient(135deg, #0052CC, #003D99)',
        boxShadow: '0 8px 32px rgba(0, 102, 255, 0.45)',
        transform: 'translateY(-3px)',
    },
    '&:active': {
        transform: 'translateY(-1px)',
    },
    '@media (max-width: 768px)': {
        width: '100%',
        justifyContent: 'center',
        padding: '14px 24px',
        fontSize: '15px',
    },
    '@media (max-width: 480px)': {
        padding: '13px 20px',
        fontSize: '14px',
    },
});

const Hero = () => {
    const demoVideoUrl = LINKS.DEMO_VIDEO_MP4;

    const handleSmoothScroll = (e, targetId) => {
        e.preventDefault();
        const target = document.querySelector(targetId);
        if (target) {
            const headerOffset = 80;
            const elementPosition = target.offsetTop;
            const offsetPosition = elementPosition - headerOffset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const therapistMarqueeItems = [
        'Physiotherapy',
        'Occupational Therapy',
        'Speech Therapy',
        'Neuro Rehabilitation',
        'Paediatric Therapy',
        'Simple Signup',
        'DPDP ready',
        'Free to join',
    ];

    return (
        <>
            {/* Separate section: Therapist registration — above hero */}
            <TherapistBannerSectionWrap component="section" aria-label="Therapist registration">
                <Container maxWidth="xl">
                    <TherapistBannerRoot>
                        <BannerStrip>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 3, flex: '1 1 320px', minWidth: 0, '@media (max-width: 768px)': { flexDirection: 'column', flex: 'none', width: '100%', gap: 2 } }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: 600, '@media (max-width: 768px)': { maxWidth: '100%', width: '100%' } }}>
                                    <Typography sx={{ fontSize: { xs: '17px', sm: '19px' }, fontWeight: 600, color: '#1E293B', lineHeight: 1.4, letterSpacing: '-0.2px', wordBreak: 'break-word' }}>
                                        Join meDZhub as a healthcare professional
                                    </Typography>
                                    <Typography sx={{ fontSize: { xs: '15px', sm: '16px' }, color: '#64748B', lineHeight: 1.55, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                        Join India's rehabilitation platform. Get verified, set your availability, and start managing sessions in minutes - free to join, DPDP ready.
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, '@media (max-width: 768px)': { width: '100%', justifyContent: 'center' } }}>
                                <GetStartedButton
                                    component={Link}
                                    to="/signupform"
                                    variant="contained"
                                    id="Join-button"
                                    data-gtm="Join-button"
                                    sx={{ '@media (max-width: 768px)': { width: '100%' } }}
                                    >
                                    Join now
                                </GetStartedButton>
                                </Box>
                            </Box>
                            <MarqueeTrack>
                                <MarqueeContent>
                                    {[...therapistMarqueeItems, ...therapistMarqueeItems].map((item, i) => (
                                        <MarqueeItem key={`${item}-${i}`}>
                                            {item}
                                            <span style={{ color: 'rgba(0, 102, 255, 0.4)', fontSize: 8 }}>●</span>
                                        </MarqueeItem>
                                    ))}
                                </MarqueeContent>
                            </MarqueeTrack>
                        </BannerStrip>
                    </TherapistBannerRoot>
                </Container>
            </TherapistBannerSectionWrap>

            {/* Hero section */}
            <HeroSection>
                <Container maxWidth="xl">
                <HeroGrid>
                    <HeroContent>
                        <HeroTitle variant="h1">
                        You can't manage <br />
                            <GradientText>what you can't verify.</GradientText>
                        </HeroTitle>
                        <HeroSubtitle>
                        meDZhub gives rehabilitation and home 
                            healthcare providers in India one system 
                            where every visit is verified, every session 
                            documented on-site, and every payout 
                            comes from that record. Not from what a 
                        therapist reported.
                        </HeroSubtitle>
                        <HeroSubtitle>Built specifically for providers managing 
                        field and in-house teams.</HeroSubtitle>
                        <HeroCTA>
                            <PrimaryButton
                                variant="contained"
                                component={Link}
                                to="/demo"
                                id="Book-demo-button"
                                data-gtm="Book-demo-button"
                            >
                                Book a demo
                            </PrimaryButton>
                            <GhostButton 
                                variant="outlined"
                                component="a"
                                href="https://wa.me/919187644794"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Talk to us on WhatsApp
                            </GhostButton>
                        </HeroCTA>
                    </HeroContent>
                    <HeroVisual>
                        <VideoFrame id="demo-video">
                            <video
                                src={demoVideoUrl}
                                controls
                                muted
                                loop
                                preload="auto"
                                playsInline
                            >
                                Your browser does not support the video tag.
                            </video>
                        </VideoFrame>
                    </HeroVisual>
                </HeroGrid>
            </Container>
            </HeroSection>
        </>
    );
};

export default Hero;
